import React, { useContext, useEffect, useState } from "react";
import { ContainerFreteGratisListagem } from "./styles";
import { ConfiguracoesContext } from "../../../contexts/Configuracoes/ConfiguracoesContext";
import Titulo from "../../Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import { Toggle } from "../../Toggle/Toggle";
import { copy } from "../../../services/Functions";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { currencyMask } from "../../../services/Masks";
import Table from "../../Table/Table";
import { Select } from "../../Select/index";
import Paginacao from "../../Paginacao";
import { Input } from "../../Input";

interface FreteGratisListagemProps {
  onEditarFrete: (index: number) => void;
  configIn?: "empresa" | "configuracoes";
  onNovoFrete?: () => void;
  /** ação exibida no centro do rodapé, na mesma linha da paginação */
  botaoAdicionar?: React.ReactNode;
}

function formatarCep(cep) {
  return cep.toString().replace(/(\d{5})(\d{3})/, "$1-$2");
}

function normalizarBusca(texto: string) {
  return texto.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

const FreteGratisListagem: React.FC<FreteGratisListagemProps> = ({
  configIn = 'empresa',
  onEditarFrete,
  onNovoFrete,
  botaoAdicionar,
}) => {
  const { fretes_gratis, setFretes_gratis } = useContext(ConfiguracoesContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [itensPorPagina] = useState(20);
  const [filtroAtivo, setFiltroAtivo] = useState<'todos' | 'ativos' | 'inativos'>('ativos');
  const [busca, setBusca] = useState("");
  const [buscaDigitada, setBuscaDigitada] = useState("");

  const isFreteAtivo = (ativo: any): boolean => {
    return ativo === true || ativo === 1 || ativo === "1" || ativo === "true";
  };

  const tableColumns = [
    { 
      key: "valor_minimo", 
      label: "Valor Mínimo",
      mask: (value: string) => currencyMask((Number.parseInt(value)).toString())
    },
    { 
      key: "regiao", 
      label: "Região",
      mask: (value: string) => value
    },
    { 
      key: "acoes", 
      label: "Ações",
      mask: (value: any) => value
    }
  ];

  const fretesFiltrados = fretes_gratis.filter(frete => {
    if (filtroAtivo === 'ativos') {
      return isFreteAtivo(frete.ativo);
    } else if (filtroAtivo === 'inativos') {
      return !isFreteAtivo(frete.ativo);
    }
    return true;
  }).filter(frete => {
    const termo = normalizarBusca(busca);
    if (!termo) return true;

    const regiao = frete.brasil_inteiro
      ? "Brasil inteiro"
      : (frete.por_estado?.estados || []).join(", ");
    if (normalizarBusca(regiao).includes(termo)) return true;

    // CEP: casa com parte do CEP inicial/final ou, com o CEP completo, com a faixa que o contém
    const digitos = busca.replace(/\D/g, "");
    if (!digitos || !frete.por_cep?.ativo) return false;
    const cepInicio = (frete.por_cep.cep_inicio || "").toString().replace(/\D/g, "");
    const cepFim = (frete.por_cep.cep_fim || "").toString().replace(/\D/g, "");
    if (cepInicio.includes(digitos) || cepFim.includes(digitos)) return true;
    return (
      digitos.length === 8 &&
      cepInicio.length > 0 &&
      cepFim.length > 0 &&
      Number(digitos) >= Number(cepInicio) &&
      Number(digitos) <= Number(cepFim)
    );
  });

  // ordena por valor mínimo antes de paginar, para que a ordem seja a mesma em todas as páginas
  const fretesOrdenados = [...fretesFiltrados].sort(
    (a, b) => Number(a.valor_minimo || 0) - Number(b.valor_minimo || 0)
  );

  const totalPaginas = Math.max(1, Math.ceil(fretesOrdenados.length / itensPorPagina));

  useEffect(() => {
    setCurrentPage(1);
  }, [filtroAtivo, busca]);

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(totalPaginas);
  }, [currentPage, totalPaginas]);

  const tableData = fretesOrdenados.map((frete) => {
    const originalIndex = fretes_gratis.findIndex(f => f === frete);
    return {
      ...frete,
      regiao: frete.brasil_inteiro
        ? "Brasil inteiro"
        : `${frete.por_estado.estados.join(", ")}${frete.por_cep?.ativo 
            ? `${formatarCep(frete.por_cep.cep_inicio)} até ${formatarCep(frete.por_cep.cep_fim)}`
            : ""}`,
      acoes: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Toggle
            value={frete.ativo}
            onChange={(newValue) => {
              const copia = copy(fretes_gratis);
              copia[originalIndex].ativo = newValue;
              setFretes_gratis(copia);
            }}
          />
          <span
            onClick={() => onEditarFrete(originalIndex)}
            className={`editar fonteTexto ${!frete.ativo ? "disabled" : ""}`}
            style={{ cursor: 'pointer' }}
          >
            Editar
          </span>
        </div>
      ),
      _originalIndex: originalIndex
    };
  });

  const dadosPagina = tableData.slice(
    (currentPage - 1) * itensPorPagina,
    currentPage * itensPorPagina
  );

  const { theme } = useTheme();
  return (
    <>
      <ContainerFreteGratisListagem theme={theme} configIn={configIn}>
        <div className="containerFreteGratisListagem">
          {configIn === "empresa" && (
            <Titulo
              titulo="Frete Grátis"
              subtitulo={""}
              flexDirection="column-reverse"
            />
          )}
          <div className="textoFreteGratis fonteTexto">
            Crie regras de frete grátis com base no valor mínimo da sacola. 
            Aplique para todo o Brasil, por estado ou faixa de CEP. <br />
            Use esse recurso para incentivar compras maiores e conquistar mais clientes.
          </div>
          <div className="linha"></div>

          {fretes_gratis.length > 0 && (
            <div className="filtros-frete">
              <div className="busca-listagem">
                <Input
                  type="text"
                  icone="icons/search.svg"
                  placeholder="Pesquisar por região ou CEP"
                  value={buscaDigitada}
                  onChange={(newValue) => setBuscaDigitada(newValue)}
                  onDebounce={setBusca}
                  debounceTime={1000}
                  onKeyDown={(event) => {
                    // a listagem fica dentro do form de configurações; Enter não deve salvar
                    if (event.key === "Enter") event.preventDefault();
                  }}
                />
              </div>
              <Select
                value={filtroAtivo}
                onChange={(newValue) => setFiltroAtivo(newValue as 'todos' | 'ativos' | 'inativos')}
                options={[
                  { name: "Ativos", value: "ativos" },
                  { name: "Inativos", value: "inativos" },
                  { name: "Todos", value: "todos" }
                ]}
                className="select-filtro-fretes"
              />
            </div>
          )}

          <div className="freteGratisContainer">
            {tableData.length > 0 ? (
              <Table
                columns={tableColumns}
                data={dadosPagina}
                sortable={true}
                selectable={false}
                button={false}
                page={1}
                itensPorPagina={itensPorPagina}
              />
            ) : (
              <div className="fonteTexto" style={{textAlign: 'center', padding: '2rem', color: '#666'}}>
                {fretes_gratis.length === 0
                  ? 'Nenhum frete grátis cadastrado'
                  : busca.trim()
                  ? 'Nenhum frete grátis encontrado para a pesquisa'
                  : `Nenhum frete grátis ${filtroAtivo === 'ativos' ? 'ativo' : 'inativo'} encontrado`
                }
              </div>
            )}
          </div>
          {(totalPaginas > 1 || botaoAdicionar) && (
            <div className="rodapeListagem">
              <div className="paginacaoListagem">
                {totalPaginas > 1 && (
                  <Paginacao
                    currentPage={currentPage}
                    totalPages={totalPaginas}
                    onPageChange={setCurrentPage}
                  />
                )}
              </div>
              <div className="adicionarListagem">{botaoAdicionar}</div>
              <div></div>
            </div>
          )}
          {configIn === "empresa" && (
            <div
              className="novo fonteTexto"
              onClick={() => {
                onNovoFrete && onNovoFrete();
              }}
            >
              Configure um novo frete grátis
            </div>
          )}
        </div>
      </ContainerFreteGratisListagem>
    </>
  );
};
export default FreteGratisListagem;
